/**
 * One instruction: its narration, lifecycle status, structured result or error,
 * and the live log. A running instruction can be cancelled from here (a
 * `process.kill` targeting it), matching the "stuck step is a normal process you
 * can interrupt" design. The log is `live` until the instruction reaches a
 * terminal status, at which point the viewer stops expecting new chunks.
 */
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Alert, Button, Chip, Divider, Paper, Stack, Typography } from '@mui/material';
import {
    COLLECTION,
    INSTRUCTION_KIND,
    isInflightStatus,
    narrationFor,
    type HostRole,
} from '@finvari/deployer';
import { useInstruction } from '../hooks';
import { LogViewer } from '../components/LogViewer';
import { archivePathFor } from '../lib/instructionActivity';
import { createInstruction } from '../lib/createInstruction';
import { dbForRole } from '../firebase';
import { useOperator } from '../auth';

function isRole(v: string | undefined): v is HostRole {
    return v === 'prepare' || v === 'release';
}

export function InstructionPage() {
    const { host, id } = useParams();
    const operator = useOperator();
    const [cancelState, setCancelState] = useState<{ sent?: boolean; error?: string }>({});

    if (!isRole(host) || !id) {
        return <Alert severity="error">Unknown instruction address.</Alert>;
    }

    const { value: instruction, loading } = useInstruction(host, id);

    if (loading) {
        return <Typography color="text.secondary">Loading…</Typography>;
    }

    // Loaded and absent is a different answer from not loaded, and it used to render
    // as a permanent "Loading…" — a mistyped or expired link looked like a slow one.
    if (!instruction) {
        return <Alert severity="warning">No instruction {id} on the {host} host.</Alert>;
    }

    const live = isInflightStatus(instruction.status);
    const title = safeTitle(instruction.kind);
    const archivePath = archivePathFor({ ...instruction, id }, host);

    // Cancel is itself an instruction (`process.kill`); this page only sees the
    // effect when the host marks *this* instruction cancelled, so say the request
    // went out in the meantime.
    const cancel = async () => {
        setCancelState({});
        const result = await createInstruction(dbForRole(host), host, INSTRUCTION_KIND.processKill, { instructionId: id }, operator);
        setCancelState(result.ok ? { sent: true } : { error: result.error });
    };

    return (
        <Stack spacing={2}>
            <Stack direction="row" spacing={2} alignItems="center">
                <Typography variant="h5">{title}</Typography>
                <Chip label={instruction.status} />
                <Chip variant="outlined" label={`${host} host`} />
                {instruction.status === 'running' &&
                    (cancelState.sent ? (
                        <Typography variant="body2" color="text.secondary">
                            Cancel requested — waiting for the host to stop the process.
                        </Typography>
                    ) : (
                        <Button color="warning" onClick={() => void cancel()}>
                            Cancel
                        </Button>
                    ))}
            </Stack>
            {cancelState.error && <Alert severity="error">Cancel failed: {cancelState.error}</Alert>}

            <Typography variant="body2" color="text.secondary">
                {describe(instruction.kind)}
            </Typography>

            {instruction.rejectedReason && <Alert severity="warning">{instruction.rejectedReason}</Alert>}
            {instruction.error && <Alert severity="error">{instruction.error}</Alert>}
            {instruction.status === 'succeeded' && instruction.result && (
                <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                        Result
                    </Typography>
                    <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{JSON.stringify(instruction.result, null, 2)}</pre>
                </Paper>
            )}

            <Divider />
            <Typography variant="subtitle1">Log</Typography>
            <LogViewer db={dbForRole(host)} instructionId={id} archivePath={archivePath} live={live} />
            <Typography variant="caption" color="text.secondary">
                {COLLECTION.instructions}/{id}
            </Typography>
        </Stack>
    );
}

function safeTitle(kind: string): string {
    try {
        return narrationFor(kind as never).title;
    } catch {
        return kind;
    }
}

function describe(kind: string): string {
    try {
        return narrationFor(kind as never).what;
    } catch {
        return '';
    }
}
